import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { TextArea } from "@/components/base/textarea/textarea";
import { SlideOver } from "@/components/application/slideout-menus/slide-over";
import {
  anomalyResolutionSchema,
  ANOMALY_RESOLUTION_ACTIONS,
  RESOLUTION_REASON_MIN_LENGTH,
  type AnomalyResolutionAction,
  type AnomalyResolutionInput,
  type AnomalyRow,
} from "../../schemas/anomalyResolution";

/**
 * UC-007 Phase 20 — Admin anomaly resolution Slide-over (REQ-UI-024, REQ-SEC-019).
 *
 * Pattern (Slide-over s povinným reason ≥ 10 znaků):
 *   1. Admin klikne na row v Data Table → open=true s předanou anomaly.
 *   2. Vybere `resolution_action` (radio) — confirmed_match / rejected_match /
 *      manual_pair / marked_for_refund / ignored.
 *   3. Napíše `reason` v textarea — min 10 znaků (REQ-SEC-019 + Zod validace).
 *   4. Submit → mutation POST /api/admin/anomalies/{id}/resolve.
 *   5. Server validuje znovu (REASON_REQUIRED 422); UI ukáže serverError.
 *
 * Accessibility: SlideOver focus trap + Esc dismiss. Form má noValidate + Zod
 * client-side; submit button má `aria-disabled` při submitting.
 */

export interface AnomalyResolutionSlideOverProps {
  open: boolean;
  anomaly: AnomalyRow | null;
  submitting?: boolean;
  serverError?: string | null;
  onClose: () => void;
  onSubmit: (anomalyId: string, input: AnomalyResolutionInput) => void;
}

/**
 * Severity → sémantická varianta (legacy slovník error|warning|info|default).
 * Konzument admin/anomalies.tsx si tuto variantu mapuje na nativní Badge color
 * vlastním adaptérem, proto zde zachováváme stejný výstupní slovník.
 */
export type SeverityVariant = "default" | "info" | "success" | "warning" | "error";

/** Mapování sémantické varianty na nativní UUI Badge color (pro lokální Badge). */
function badgeColorFor(
  variant: SeverityVariant,
): "gray" | "blue" | "success" | "warning" | "error" {
  switch (variant) {
    case "error":
      return "error";
    case "warning":
      return "warning";
    case "info":
      return "blue";
    case "success":
      return "success";
    default:
      return "gray";
  }
}

const ACTION_LABELS: Record<AnomalyResolutionAction, string> = {
  confirmed_match: "Potvrdit párování",
  rejected_match: "Zamítnout párování",
  manual_pair: "Ručně spárovat",
  marked_for_refund: "Vrátit plátci",
  ignored: "Ignorovat (false positive)",
};

export function AnomalyResolutionSlideOver({
  open,
  anomaly,
  submitting,
  serverError,
  onClose,
  onSubmit,
}: AnomalyResolutionSlideOverProps) {
  const form = useForm<AnomalyResolutionInput>({
    resolver: zodResolver(anomalyResolutionSchema),
    defaultValues: { action: "marked_for_refund", reason: "" },
  });

  useEffect(() => {
    if (open) {
      // Reset form whenever a new anomaly is selected.
      form.reset({
        action: defaultActionFor(anomaly),
        reason: "",
      });
    }
  }, [open, anomaly, form]);

  function handleSubmit(input: AnomalyResolutionInput): void {
    if (!anomaly) return;
    onSubmit(anomaly.anomaly_id, input);
  }

  return (
    <SlideOver
      open={open}
      title="Vyřešit anomálii"
      onClose={onClose}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={submitting}>
            Zrušit
          </Button>
          <Button
            type="submit"
            form="anomaly-resolution-form"
            color="primary"
            isDisabled={submitting || !anomaly}
            data-testid="anomaly-resolution-submit"
            aria-disabled={submitting || !anomaly}
          >
            {submitting ? "Ukládám…" : "Vyřešit"}
          </Button>
        </>
      }
    >
      {anomaly == null ? (
        <p>Žádná anomálie není vybraná.</p>
      ) : (
        <div className="flex flex-col gap-4">
          <header
            className="flex flex-col gap-2 rounded-xl border border-secondary bg-secondary px-4 py-3"
            data-testid="anomaly-resolution-header"
          >
            <div className="flex flex-wrap gap-2">
              <Badge color={badgeColorFor(severityVariant(anomaly.severity))}>
                {severityLabel(anomaly.severity)}
              </Badge>
              <Badge color="gray">{typeLabel(anomaly.anomaly_type)}</Badge>
              {anomaly.score != null && (
                <Badge color="blue">Score {anomaly.score}/100</Badge>
              )}
            </div>
            <dl className="m-0 grid grid-cols-2 gap-x-4 gap-y-2">
              <div className="flex flex-col">
                <dt className="text-xs uppercase tracking-wide text-tertiary">Částka</dt>
                <dd className="m-0 font-semibold">{formatKc(anomaly.amount_kc)}</dd>
              </div>
              <div className="flex flex-col">
                <dt className="text-xs uppercase tracking-wide text-tertiary">VS</dt>
                <dd className="m-0 font-semibold">{anomaly.variable_symbol ?? "—"}</dd>
              </div>
              <div className="flex flex-col">
                <dt className="text-xs uppercase tracking-wide text-tertiary">Plátce</dt>
                <dd className="m-0 font-semibold">{anomaly.counter_party_name ?? "—"}</dd>
              </div>
              <div className="flex flex-col">
                <dt className="text-xs uppercase tracking-wide text-tertiary">Vytvořeno</dt>
                <dd className="m-0 font-semibold">{formatDateTime(anomaly.created_at)}</dd>
              </div>
            </dl>
          </header>

          {serverError && (
            <InlineAlert variant="error" title="Chyba při ukládání">
              {serverError}
            </InlineAlert>
          )}

          <form
            id="anomaly-resolution-form"
            noValidate
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <fieldset className="flex flex-col gap-1.5 border-0 p-0">
              <legend className="mb-1.5 font-semibold">Akce</legend>
              {ANOMALY_RESOLUTION_ACTIONS.map((action) => (
                <label
                  key={action}
                  className="flex min-h-touch cursor-pointer items-center gap-2 rounded-md border border-secondary px-2 py-1 hover:bg-secondary"
                  data-testid={`anomaly-action-${action}`}
                >
                  <input
                    type="radio"
                    value={action}
                    {...form.register("action")}
                  />
                  <span>{ACTION_LABELS[action]}</span>
                </label>
              ))}
            </fieldset>

            <Controller
              control={form.control}
              name="reason"
              render={({ field, fieldState }) => (
                <TextArea
                  label={`Důvod (min ${RESOLUTION_REASON_MIN_LENGTH} znaků)`}
                  isRequired
                  rows={4}
                  isInvalid={Boolean(fieldState.error)}
                  hint={fieldState.error?.message ?? undefined}
                  placeholder="Popište proč se rozhodlo takto (záznam do auditní stopy)"
                  data-testid="anomaly-resolution-reason"
                  name={field.name}
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  textAreaRef={field.ref}
                />
              )}
            />
          </form>
        </div>
      )}
    </SlideOver>
  );
}

function defaultActionFor(anomaly: AnomalyRow | null): AnomalyResolutionAction {
  if (!anomaly) return "marked_for_refund";
  switch (anomaly.anomaly_type) {
    case "cross_account":
    case "duplicate":
      return "marked_for_refund";
    case "amount_mismatch":
      return "manual_pair";
    case "heuristic_proposed":
      return "confirmed_match";
    case "unmatched":
      return "ignored";
    default:
      return "marked_for_refund";
  }
}

export function severityVariant(severity: string): SeverityVariant {
  switch (severity) {
    case "critical":
      return "error";
    case "high":
      return "warning";
    case "medium":
      return "info";
    case "low":
      return "default";
    default:
      return "default";
  }
}

export function severityLabel(severity: string): string {
  switch (severity) {
    case "critical":
      return "Kritická";
    case "high":
      return "Vysoká";
    case "medium":
      return "Střední";
    case "low":
      return "Nízká";
    default:
      return severity;
  }
}

export function typeLabel(type: string): string {
  switch (type) {
    case "cross_account":
      return "Cross-account";
    case "duplicate":
      return "Duplicitní platba";
    case "amount_mismatch":
      return "Neshoda částky";
    case "heuristic_proposed":
      return "Heuristic match";
    case "unmatched":
      return "Bez kandidáta";
    default:
      return type;
  }
}

function formatKc(amount: number): string {
  return (
    amount.toLocaleString("cs-CZ", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }) + " Kč"
  );
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString("cs-CZ");
  } catch {
    return iso;
  }
}
